import React, { useReducer, useState, useMemo, useEffect, useCallback } from "react";
import { Dropdown, Button, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import Select from "react-select";
import { FaPlusCircle } from "react-icons/fa";
import MyAlert from '../../Components/Reusable/Alert/Alert';
import {
  AddService,
  GetServices,
  ActivateService,
  DisableService,
  DeleteService,
  EditService,
  GetServiceDurations,
  GetConfig,
  getCroppedImg
} from './Requests';
import { usePagination } from "../../Components/Reusable/TablePagination/Functions";
import TablePaginationFooter from "../../Components/Reusable/TablePagination/TableFooter";
import { FaExclamationTriangle } from "react-icons/fa";
import {
  notifySuccess,
  notifyError
} from '../../Response/ResposeHandler';
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Cropper from 'react-easy-crop';
import './Services.css';
import CustomButton from "../../Components/Reusable/Button/CustomButton";
import { formatMinor, getCurrencySymbol } from "../../Components/Reusable/CurrencyFormatter/CurrencyFormatter";
import { useTranslation } from "react-i18next";

const ManageServices = () => {
  const { t } = useTranslation('services');

  const [services, setServices] = useState([]);
  const [timeSelection, setTimeSelection] = useState([]);
  const [serviceImage, setServiceImage] = useState("data:image/jpeg;base64," + null);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [serviceId, setServiceId] = useState(null);
  const [serviceStatus, setServiceStatus] = useState();
  const [isEdit, setIsEdit] = useState(false);
  const [serviceName, setServiceName] = useState('');
  const [acceptOnlinePayment, setAcceptOnlinePayment] = useState(false);
  const [serviceDuration, setServiceDuration] = useState(0);
  const [servicePrice, setServicePrice] = useState(0);
  const [serviceDescription, setServiceDescription] = useState('');
  const [isAddServiceButtonLoading, setIsAddServiceButtonLoading] = useState(false);
  const [isActivateServiceButtonLoading, setIsActivateServiceButtonLoading] = useState(false);
  const [isDisableServiceButtonLoading, setIsDisbaleServiceButtonLoading] = useState(false);
  const [isDeleteServiceButtonLoading, setIsDeleteServiceButtonLoading] = useState(false);
  const [priceToPay, setPriceToPay] = useState(0.20);
  let errorsObj = { serviceName: '', serviceDuration: '', servicePrice: '', serviceDescription: '' };
  const [errors, setErrors] = useState(errorsObj);
  const [config, setConfig] = useState();
  const [payMax, setPayMax] = useState();
  const [payReturn, setPayReturn] = useState(0);
  const [allowCancellation, setAllowCancellation] = useState(true);
  const initialState = false;

  const FEES = {
    uk:   { pct: 0.015,  flatMinor: 20, label: 'UK-issued (1.5% + £0.20)' },
    eu:   { pct: 0.025,  flatMinor: 20, label: 'EU-issued (2.5% + £0.20)' },
    intl: { pct: 0.0325, flatMinor: 20, label: 'International (3.25% + £0.20)' },
  };

  const reducer = (state, action) => {
    switch (action.type) {
      case 'addServiceOpen':  return { ...state, sendMessage: true };
      case 'addServiceClose': return { ...state, sendMessage: false };
      default:                return state;
    }
  };

  useEffect(() => {
    GetServicesWrapper();
    GetServiceDurationsWrapper();
  }, []);

  const total = services?.length ?? 0;
  const { page, setPage, pageSize, setPageSize, start, end, canPrev, canNext } =
    usePagination(total);
  const paged = useMemo(() => (services ? services.slice(start, end) : []), [services, start, end]);

  const ClearIndicatorStyles = (base, state) => ({
    ...base,
    cursor: "pointer",
    color: state.isFocused ? "#525252" : "#7e7e7e",
  });

  const ClearIndicator = (props) => {
    const { getStyles, innerProps: { ref, ...restInnerProps } } = props;
    return (
      <div {...restInnerProps} ref={ref} style={getStyles("clearIndicator", props)}>
        <div style={{ padding: "0px 5px" }}>{t('select.clear')}</div>
      </div>
    );
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropDone = async () => {
    const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
    setServiceImage(croppedImage);
    setImageSrc(null);
  };

  const handleCropCancel = () => {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  const netAfterFees = (amountMinor, { pct, flatMinor }) =>
    Math.max(0, amountMinor - Math.round(amountMinor * pct) - flatMinor);

  const StripeFeeBreakdown = ({ amountMinor, currency = 'GBP', locale = 'en-GB' }) => {
    const sym = getCurrencySymbol(currency, locale);
    return (
      <div className="sm-help">
        <div className="fee-rows">
          {Object.entries(FEES).map(([key, fee]) => (
            <div className="fee-row" key={key}>
              <div className="fee-row__label">
                {fee.label} — {t('fee.youReceive')} <strong>{formatMinor(netAfterFees(amountMinor, fee), currency, locale)}</strong>
              </div>
            </div>
          ))}
        </div>
        <div className="sm-help--muted" style={{ marginTop: 6 }}>
          {t('fee.fxNote')}
        </div>
      </div>
    );
  };

  function onSubmitClick(e) {
    e.preventDefault();

    let error = false;
    const errorObj = { ...errorsObj };
    if (serviceName === '') {
      errorObj.serviceName = t('validation.nameRequired');
      error = true;
    }
    if (serviceDuration === null || serviceDuration === undefined) {
      errorObj.serviceDuration = t('validation.durationRequired');
      error = true;
    } else {
      if (serviceDuration.value === 0 || serviceDuration.value === null || serviceDuration.value === '') {
        errorObj.serviceDuration = t('validation.durationRequired');
        error = true;
      }
    }
    if (servicePrice === '' || servicePrice === null || servicePrice <= 0) {
      errorObj.servicePrice = t('validation.priceRequired');
      error = true;
    }
    setErrors(errorObj);
    if (error) return;

    setIsAddServiceButtonLoading(true);

    const serviceImageFile = base64ToFile(serviceImage, "serviceImage.jpg");

    if (isEdit) {
      console.log(acceptOnlinePayment)
      EditService(
        serviceId,
        serviceName,
        serviceDuration.value,
        servicePrice,
        serviceDescription,
        serviceStatus,
        acceptOnlinePayment,
        priceToPay * 100,
        serviceImage === "data:image/jpeg;base64,null" ? null : serviceImageFile,
        allowCancellation
      ).then(() => {
        notifySuccess(t('toasts.serviceEdited'));
        dispatch({ type: 'addServiceClose' });
        GetServicesWrapper();
      }).catch(() => {
        notifyError(t('toasts.editFailed'));
      }).finally(() => {
        setIsAddServiceButtonLoading(false);
      });
    } else {
      AddService(
        serviceName,
        serviceDuration.value,
        servicePrice,
        serviceDescription,
        acceptOnlinePayment,
        priceToPay * 100,
        serviceImage === "data:image/jpeg;base64,null" ? null : serviceImageFile,
        allowCancellation
      ).then(() => {
        notifySuccess(t('toasts.serviceAdded'));
        dispatch({ type: 'addServiceClose' });
        GetServicesWrapper();
      }).catch((err) => {
        if (err.response.data === "Service limit reached")
        {
            MyAlert.fire({
              title: t('alerts.serviceLimit.title'),
              text: t('alerts.serviceLimit.text'),
              icon: 'warning',
              showCancelButton: false,
              confirmButtonColor: 'var(--primary)',
              cancelButtonColor: '#aaa',
              confirmButtonText: t('alerts.serviceLimit.confirmButton')
            }).then((result) => {
              dispatch({ type: 'addServiceClose' });
            })
        }
        else{
          notifyError(t('toasts.addFailed'));
        }
      }).finally(() => {
        setIsAddServiceButtonLoading(false);
      });
    }
  }

  function base64ToFile(base64Data, filename) {
    const arr = base64Data.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], filename, { type: mime });
  }

  function handleInputChange(e) {
    let newValue = parseFloat(e.target.value);
    if (isNaN(newValue)) newValue = 0.20;
    if (newValue > payMax) newValue = payMax;
    setPriceToPay(parseFloat(e.target.value));
    setPayReturn(Math.abs(Number((parseFloat(e.target.value)) - (((parseFloat(e.target.value) * 1.5) / 100) + (0.20))).toFixed(2)));
  }

  function handleSliderChange(e) {
    setPriceToPay(parseFloat(e.target.value));
    setPayReturn(Math.abs(Number((parseFloat(e.target.value)) - (((parseFloat(e.target.value) * 1.5) / 100) + (0.20))).toFixed(2)));
  }

  function OnDisableService(serviceId, name, description, duration, price) {
    setIsDisbaleServiceButtonLoading(true);
    DisableService(serviceId, name, description, duration, price).then(() => {
      notifySuccess(t('toasts.serviceDisabled'));
      GetServicesWrapper();
    }).catch(() => {
      notifyError(t('toasts.disableFailed'));
    }).finally(() => {
      setIsDisbaleServiceButtonLoading(false);
    });
  }

  function OnActivateService(serviceId, name, description, duration, price) {
    setIsActivateServiceButtonLoading(true);
    ActivateService(serviceId, name, description, duration, price).then(() => {
      notifySuccess(t('toasts.serviceActivated'));
      GetServicesWrapper();
    }).catch(() => {
      notifyError(t('toasts.activateFailed'));
    }).finally(() => {
      setIsActivateServiceButtonLoading(false);
    });
  }

  function OnDeleteService(serviceId, name, description, duration, price) {
    setIsDeleteServiceButtonLoading(true);
        MyAlert.fire({
          title: t('alerts.confirmationDelete.message'),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: 'var(--primary)',
          cancelButtonColor: '#aaa',
          confirmButtonText: t('alerts.confirmationDelete.confirmDelete')
        }).then((result) => {
          if (result.isConfirmed) {
            DeleteService(serviceId, name, description, duration, price).then(() => {
              notifySuccess(t('toasts.serviceRemoved'));
              GetServicesWrapper();
            }).catch(() => {
              notifyError(t('toasts.removeFailed'));
            }).finally(() => {
              setIsDeleteServiceButtonLoading(false);
            });
          }
          else{
            setIsDeleteServiceButtonLoading(false);
          }
        })
  }

  function OnEditService(serviceId, name, description, duration, price, status, acceptOnlinePayment, priceToPay, image, allowCancellation) {
    console.log(priceToPay)
    console.log(acceptOnlinePayment)
    setServiceId(serviceId);
    setAllowCancellation(allowCancellation);
    setIsEdit(true);
    // if (acceptOnlinePayment && config?.acceptOnlinePayments)
    // {
    //   setAcceptOnlinePayment(true)
    // } else {
    //   setAcceptOnlinePayment(false)
    // }
    setAcceptOnlinePayment(!!acceptOnlinePayment);
    setServiceName(name);
    setServiceDescription(description);
    setServiceDuration(timeSelection.filter(item => item.value === duration)[0]);
    setServicePrice(price);
    setServiceStatus(status);
    setPriceToPay(Number(priceToPay / 100).toFixed(2));
    setPayMax(parseFloat(price));
    setServiceImage("data:image/jpeg;base64," + image);
  }

  function GetServicesWrapper() {
    setIsLoading(true);
    GetServices().then((res) => {
      setServices(res.data);
    }).catch(() => {
      notifyError(t('toasts.retrieveFailed'));
    }).finally(() => {
      GetConfigWrapper();
    });
  }

  function GetConfigWrapper() {
    GetConfig().then((res) => {
      setConfig(res.data);
      // setAcceptOnlinePayment(res.data.acceptOnlinePayments);
    }).catch(() => {
    }).finally(() => {
      setIsLoading(false);
    });
  }

  function GetServiceDurationsWrapper() {
    GetServiceDurations().then((res) => {
      setTimeSelection(res.data);
    }).catch(() => {
      notifyError(t('toasts.durationsFailed'));
    });
  }

  function resetModal() {
    setIsEdit(false);
    setServiceName('');
    setAllowCancellation(true);
    setAcceptOnlinePayment(false);
    setServiceDuration(null);
    setServicePrice(null);
    setServiceDescription('');
    setErrors(errorsObj);
    setPriceToPay(0.20);
    setPayMax();
    setPayReturn(0);
    setServiceImage("data:image/jpeg;base64," + null);
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setImageSrc(imageUrl);
    }
  };

  function DeleteServiceImage(e) {
    e.preventDefault();
    setServiceImage("data:image/jpeg;base64," + null);
  }

  const allowKeys = new Set(["ArrowUp", "ArrowDown", "Tab", "Home", "End"]);
  function guardNumberTyping(e) {
    if (allowKeys.has(e.key)) return;
    if (e.key === "Tab" && e.shiftKey) return;
    e.preventDefault();
  }

  return (
    <div className="h-80 services-wrapper">
      <ToastContainer />
      {isLoading ? (
        <div className="custom-loader-wrapper">
          <div className="custom-loader"></div>
        </div>
      ) : (
        <div>
          <div className="mb-3 add-service-button" style={{ display: 'grid', justifyContent: 'end' }}>
            <CustomButton
              icon={<FaPlusCircle />}
              className="app-btn--sm"
              onClick={() => { dispatch({ type: 'addServiceOpen' }); resetModal();  }}
              label={t('list.addServiceButton')}
              loading={false}
            />
          </div>

          {services.length === 0 ? (
            <div className="sm-dull">{t('list.noServices')}</div>
          ) : (
            <div className="services-container">
              <div className="data-table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th className="align-middle ps-2">{t('list.table.serviceName')}</th>
                      <th className="align-middle ps-2">{t('list.table.serviceDuration')}</th>
                      <th className="align-middle ps-5">{t('list.table.price')}</th>
                      <th className="align-middle ps-5">{t('list.table.onlinePayment')}</th>
                      <th className="align-middle ps-4">{t('list.table.status')}</th>
                      <th className="align-middle text-end">{t('list.table.action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((service) => (
                      <tr key={service.serviceId} className="btn-reveal-trigger">
                        <td className="py-2">
                          <span className="table-field-ellipsis" title={service.name}>
                            {service.name}
                          </span>
                        </td>
                        <td className="py-2">{service.timeLabel}</td>
                        <td className="py-2 ps-5">{formatMinor(service.price, config.currency)}</td>
                        {/* <td className="py-2 ps-5">
                          {!service.acceptOnlinePayment ? "-" : formatMinor(service.onlinePaymentAmount, config.currency)}
                        </td> */}
                        <td className="py-2 ps-5">
                          {!service.acceptOnlinePayment ? (
                            "-"
                          ) : (
                            <div className="payment-cell">
                              <span className={`payment-cell__amount ${!config?.acceptOnlinePayments ? 'is-inactive' : ''}`}>
                                {formatMinor(service.onlinePaymentAmount, config.currency)}
                              </span>

                              {!config?.acceptOnlinePayments && (
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip>
                                      {t('list.table.tooltipPayment')}
                                    </Tooltip>
                                  }
                                >
                                  <span className="payment-cell__warning" aria-label="Payments inactive globally">
                                    <FaExclamationTriangle />
                                  </span>
                                </OverlayTrigger>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="py-2 ps-4 align-middle">
                          <span className={`badge badge-sm light ${service.status === 1 ? 'badge-success' : 'badge-danger'}`}>
                            {service.status === 1 ? t('list.status.active') : t('list.status.disabled')}
                          </span>
                        </td>
                        <td className="py-2 text-end">
                          <Dropdown autoClose={true} className="dropdown text-sans-serif">
                            <Dropdown.Toggle
                              variant=""
                              className="btn tp-btn-light sharp i-false"
                              type="button"
                              id={`order-dropdown-${service.serviceId}`}
                              data-toggle="dropdown"
                              aria-haspopup="true"
                              aria-expanded="false"
                              drop="up"
                            >
                              <span>
                                <svg xmlns="http://www.w3.org/2000/svg" width="18px" height="18px" viewBox="0 0 24 24" version="1.1">
                                  <g stroke="none" strokeWidth={1} fill="none" fillRule="evenodd">
                                    <rect y={0} width={24} height={24}/>
                                    <circle fill="#000000" cx={5} cy={12} r={2}/>
                                    <circle fill="#000000" cx={12} cy={12} r={2}/>
                                    <circle fill="#000000" cx={19} cy={12} r={2}/>
                                  </g>
                                </svg>
                              </span>
                            </Dropdown.Toggle>
                            <Dropdown.Menu
                              align="end"
                              renderOnMount
                              popperConfig={{
                                strategy: 'fixed',
                                modifiers: [
                                  { name: 'computeStyles', options: { adaptive: false } },
                                  { name: 'preventOverflow', options: { boundary: 'viewport' } },
                                  { name: 'flip', options: { fallbackPlacements: ['top', 'bottom'] } },
                                ],
                              }}
                              className="dropdown-menu dropdown-menu-right border py-0"
                              aria-labelledby={`order-dropdown-${service.serviceId}`}
                            >
                              {service.status === 1 ? (
                                isDisableServiceButtonLoading
                                  ? <span style={{ marginLeft: '1rem' }} className="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>
                                  : <Dropdown.Item onClick={() => { OnDisableService(service.serviceId, service.name, service.description, service.duration, service.price); }} className="dropdown-item">
                                      {t('list.dropdown.disable')}
                                    </Dropdown.Item>
                              ) : (
                                isActivateServiceButtonLoading
                                  ? <span style={{ marginLeft: '1rem' }} className="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>
                                  : <Dropdown.Item onClick={() => { OnActivateService(service.serviceId, service.name, service.description, service.duration, service.price); }} className="dropdown-item">
                                      {t('list.dropdown.activate')}
                                    </Dropdown.Item>
                              )}
                              <Dropdown.Item
                                className="dropdown-item"
                                onClick={() => {
                                  dispatch({ type: 'addServiceOpen' });
                                  OnEditService(
                                    service.serviceId,
                                    service.name,
                                    service.description,
                                    service.duration,
                                    service.price,
                                    service.status,
                                    service.acceptOnlinePayment,
                                    service.onlinePaymentAmount,
                                    service.serviceImage,
                                    service.allowCancellation
                                  );
                                }}
                              >
                                {t('list.dropdown.edit')}
                              </Dropdown.Item>
                              {isDeleteServiceButtonLoading
                                ? <span style={{ marginLeft: '1rem' }} className="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>
                                : <Dropdown.Item onClick={() => { OnDeleteService(service.serviceId, service.name, service.description, service.duration, service.price); }} className="dropdown-item text-danger">
                                    {t('list.dropdown.delete')}
                                  </Dropdown.Item>}
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {services.length > 8 ? (
            <TablePaginationFooter
              total={total}
              page={page}
              pageSize={pageSize}
              setPage={setPage}
              setPageSize={setPageSize}
              canPrev={canPrev}
              canNext={canNext}
            />
          ) : null}

          {/* Add/Edit modal */}
          <Modal tabIndex="-1" className="modal fade" show={state.sendMessage} centered>
            <div className="sm-modal">
              <div className="sm-modal__header">
                <h5 className="sm-modal__title">{isEdit ? t('modals.form.titleEdit') : t('modals.form.titleAdd')}</h5>
                <Button variant="" type="button" className="btn-close" data-dismiss="modal" onClick={() => { dispatch({ type: 'addServiceClose' }); resetModal(); }}></Button>
              </div>

              <div className="sm-modal__body">
                <form className="sm-form">
                  <div className="sm-grid sm-grid--2">
                    {/* Service Name */}
                    <div className={`sm-field ${errors.serviceName ? 'is-error' : ''}`}>
                      <label className="sm-label">
                        {t('modals.form.fields.serviceName')} <span className="sm-req">*</span>
                      </label>
                      <input
                        type="text"
                        className="sm-input"
                        name="serviceName"
                        value={serviceName}
                        onChange={(e) => setServiceName(e.target.value)}
                      />
                      {errors.serviceName && <div className="sm-error">{errors.serviceName}</div>}
                    </div>

                    {/* Service Duration */}
                    <div className={`sm-field ${errors.serviceDuration ? 'is-error' : ''}`}>
                      <label className="sm-label">
                        {t('modals.form.fields.serviceDuration')} <span className="sm-req">*</span>
                      </label>
                      <Select
                        isSearchable={false}
                        closeMenuOnSelect
                        components={{ ClearIndicator }}
                        styles={{ clearIndicator: ClearIndicatorStyles }}
                        defaultValue={serviceDuration}
                        options={timeSelection}
                        onChange={(e) => setServiceDuration(e)}
                        className="sm-select"
                        classNamePrefix="rs"
                      />
                      {errors.serviceDuration && <div className="sm-error">{errors.serviceDuration}</div>}
                    </div>

                    {/* Price */}
                    <div className={`sm-field ${errors.servicePrice ? 'is-error' : ''}`}>
                      <label className="sm-label">
                        {t('modals.form.fields.price')}<span className="sm-req">*</span>
                      </label>

                      <div className="sm-input-affix">
                        <span className="sm-input-prefix" aria-hidden="true">{getCurrencySymbol(config.currency)}</span>
                        <input
                          name="servicePrice"
                          type="number"
                          step="0.01"
                          style={{ paddingLeft: "1.3rem" }}
                          className="sm-input sm-input-with-prefix"
                          defaultValue={servicePrice === null ? null : Number(servicePrice / 100).toFixed(2)}
                          onChange={(e) => setServicePrice(e.target.value * 100)}
                        />
                      </div>
                      {errors.servicePrice && <div className="sm-error">{errors.servicePrice}</div>}
                    </div>

                    {/* Description */}
                    <div className="sm-field sm-grid__full">
                      <label className="sm-label">{t('modals.form.fields.description')}</label>
                      <textarea
                        rows={3}
                        className="sm-textarea"
                        name="serviceDescription"
                        value={serviceDescription}
                        maxLength={50}
                        onChange={(e) => setServiceDescription(e.target.value)}
                      />
                      {errors.serviceDescription && <div className="sm-error">{errors.serviceDescription}</div>}
                    </div>

                    {/* Allow cancellations */}
                    <div className="sm-field" style={{ gridColumn: "1 / -1" }}>
                      <label className="sm-check-cancellation">
                        <input
                          type="checkbox"
                          className="sm-check-cancellation__input"
                          id="isBlocked"
                          checked={allowCancellation}
                          onChange={(e) => setAllowCancellation(e.target.checked)}
                        />
                        <span className="sm-check-cancellation__box" aria-hidden="true"></span>
                        <span className="sm-check__label">{t('modals.form.fields.allowCancellations')}</span>
                      </label>
                      <p className="sm-hint" style={{ fontSize: "0.9rem" }}>{t('modals.form.fields.allowCancellationsHint')}</p>
                    </div>

                    {/* Image */}
                    <div className="sm-field sm-grid__full">
                      <label className="sm-label">{t('modals.form.fields.image')}</label>

                      <div className="sm-image sm-image--stack">
                        <div className="sm-image__preview">
                          {serviceImage !== 'data:image/jpeg;base64,null'
                            ? <img src={serviceImage} alt="service" />
                            : <div className="sm-image__placeholder">{t('modals.form.fields.imagePlaceholder')}</div>}
                        </div>

                        <div className="sm-image__actions sm-image__actions--stack">
                          <label className="sm-btn sm-btn--primary">
                            {t('modals.form.fields.uploadLogo')}
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => { handleImageChange(e); e.target.value = null; }}
                              hidden
                            />
                          </label>

                          <button type="button" onClick={DeleteServiceImage} className="sm-btn sm-btn--ghost">
                            {t('modals.form.fields.deleteImage')}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Online Payments */}
                    <div className="sm-divider sm-grid__full">{t('modals.form.onlinePayments.sectionTitle')}</div>
                    {!config?.acceptOnlinePayments && (
                      <div className="sm-field" style={{ gridColumn: "1 / -1" }}>
                        <p className="sm-hint" style={{ fontSize: "0.9rem" }}>
                          You need enable 'Accept online payments' in the <a classname="link" href="/online-booking">booking portal</a> menu before you can charge online for service
                        </p>
                      </div>
                    )}

                    {config?.acceptOnlinePayments && (
                      <div className="sm-field sm-grid__full">
                        <div className="sm-toggle-row">
                          <label className={`sm-label ${!config?.acceptOnlinePayments ? 'is-disabled' : ''}`}>
                            {t('modals.form.onlinePayments.accept')}
                          </label>

                          <div className="switch-wrapper">
                            <input
                              onChange={(e) => { setAcceptOnlinePayment(e.target.checked); }}
                              className="toggle-switch"
                              type="checkbox"
                              disabled={!config?.acceptOnlinePayments}
                              checked={!!acceptOnlinePayment}
                              id="logon"
                            />
                            <label htmlFor='logon' className="switch-label" />
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {acceptOnlinePayment && config?.acceptOnlinePayments && (
                      <div className="sm-field sm-grid__full">
                        <label className="sm-label">{t('modals.form.onlinePayments.chargeAtBooking')}</label>

                        <div className="sm-price-slider">
                          <input
                            type="number"
                            className="sm-input sm-input--inline no-caret"
                            value={priceToPay === 0.20 ? Number(0.20).toFixed(2) : Number(priceToPay).toFixed(2)}
                            onChange={handleInputChange}
                            min={0.20}
                            max={servicePrice / 100}
                            step={0.10}
                            onKeyDown={guardNumberTyping}
                            onPaste={(e) => e.preventDefault()}
                            onDrop={(e) => e.preventDefault()}
                            inputMode="none"
                          />
                          <input
                            type="range"
                            className="sm-slider sm-slider--fill"
                            value={priceToPay === null ? 0 : priceToPay}
                            onChange={handleSliderChange}
                            min={0.20}
                            max={servicePrice / 100}
                            step={0.10}
                            style={{
                              '--val': `${
                                ((Math.min(Math.max(priceToPay ?? 0, 0.20), (servicePrice / 100) || 1) - 0.20) /
                                (((servicePrice / 100) || 1) - 0.20 || 1)) * 100
                              }%`
                            }}
                          />
                        </div>
                        <StripeFeeBreakdown amountMinor={priceToPay * 100} currency={config.currency} />
                      </div>
                    )}

                    {/* Actions */}
                    <div className="sm-actions sm-grid__full">
                      <CustomButton
                        className="app-btn--sm"
                        onClick={(e) => { onSubmitClick(e); }}
                        label={isEdit ? t('modals.form.actions.edit') : t('modals.form.actions.add')}
                        loading={isAddServiceButtonLoading}
                      />
                    </div>
                  </div>
                </form>
              </div>

              {/* Cropper */}
              {imageSrc && (
                <div className="cropper-modal">
                  <div className="cropper-wrapper">
                    <Cropper
                      image={imageSrc}
                      crop={crop}
                      zoom={zoom}
                      aspect={400 / 500}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                    />
                    <div className="crop-controls">
                      <button className="upload-button" onClick={handleCropDone}>
                        {t('modals.form.actions.crop')}
                      </button>
                      <button className="upload-button cancel-button" onClick={handleCropCancel}>
                        {t('modals.form.actions.cancel')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Modal>
        </div>
      )}
    </div>
  );
};

export default ManageServices;
